import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heading, Type, Image as ImageIcon, MousePointerClick, Minus, MoveVertical,
  Columns2, Trash2, Copy, GripVertical, Bold, Italic, Link2, Upload,
  AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';
import { BLOCK_TYPES, defaultBlock } from '../lib/emailBlocks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG_BASE = `${API}/admin/bulk-email/images`;
const mediaUrl = (id) => `${API}/public/media/${id}`;

const ICONS = { heading: Heading, text: Type, image: ImageIcon, button: MousePointerClick, divider: Minus, spacer: MoveVertical, columns: Columns2 };

const AlignPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]].map(([v, I]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`w-8 h-8 rounded-md border flex items-center justify-center ${value === v ? 'bg-[#0B2942] text-white border-[#0B2942]' : 'border-slate-200 text-slate-500'}`}>
        <I className="w-4 h-4" />
      </button>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm';

const ImagePicker = ({ onPick }) => {
  const [lib, setLib] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => axios.get(IMG_BASE).then((r) => setLib(r.data.images || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const removeImg = async (id, e) => {
    e.stopPropagation();
    try { await axios.delete(`${IMG_BASE}/${id}`); await load(); } catch { toast.error('Could not delete image'); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await axios.post(IMG_BASE, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onPick(mediaUrl(data.id));
      await load();
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} data-testid="builder-image-upload" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#00A79D] text-[#00A79D] text-xs font-semibold hover:bg-[#00A79D]/5 disabled:opacity-60"
        data-testid="builder-image-upload-btn">
        <Upload className="w-4 h-4" />{uploading ? 'Uploading…' : 'Upload image'}
      </button>
      {lib.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5 mt-2 max-h-32 overflow-auto" data-testid="builder-image-library">
          {lib.map((im) => (
            <div key={im.id} className="relative group aspect-square rounded-md overflow-hidden border border-slate-200 hover:border-[#00A79D]">
              <button type="button" onClick={() => onPick(mediaUrl(im.id))} title={im.filename}
                className="w-full h-full" data-testid={`builder-image-lib-${im.id}`}>
                <img src={mediaUrl(im.id)} alt={im.filename} className="w-full h-full object-cover" />
              </button>
              <button type="button" onClick={(e) => removeImg(im.id, e)} title="Delete image"
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-white/90 rounded flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100"
                data-testid={`builder-image-del-${im.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// contentEditable that commits on blur (no cursor jumps while typing)
const Editable = ({ html, onCommit, style, className, ph }) => {
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-ph={ph}
      onBlur={() => onCommit(ref.current.innerHTML)}
      className={`outline-none focus:ring-2 focus:ring-[#00A79D]/40 rounded ${className || ''}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: html || '' }}
    />
  );
};

export default function EmailBuilder({ blocks, onChange }) {
  const list = blocks || [];
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null);        // {kind:'new'|'move', type?, index?}
  const [overIndex, setOverIndex] = useState(null);

  const update = (next) => onChange(next);
  const patch = (id, props) => update(list.map((b) => (b.id === id ? { ...b, ...props } : b)));
  const remove = (id) => { update(list.filter((b) => b.id !== id)); if (selectedId === id) setSelectedId(null); };
  const duplicate = (id) => {
    const i = list.findIndex((b) => b.id === id);
    const copy = { ...list[i], id: `${list[i].id}_c${Date.now().toString(36)}` };
    update([...list.slice(0, i + 1), copy, ...list.slice(i + 1)]);
  };

  const addAt = (type, index) => {
    const nb = defaultBlock(type);
    const i = index == null ? list.length : index;
    update([...list.slice(0, i), nb, ...list.slice(i)]);
    setSelectedId(nb.id);
  };

  const moveTo = (from, to) => {
    if (from === to || from === to - 1) return;
    const arr = [...list];
    const [it] = arr.splice(from, 1);
    arr.splice(from < to ? to - 1 : to, 0, it);
    update(arr);
  };

  const onRowDragOver = (index, e) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    setOverIndex(after ? index + 1 : index);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const idx = overIndex == null ? list.length : overIndex;
    if (drag?.kind === 'new') addAt(drag.type, idx);
    else if (drag?.kind === 'move') moveTo(drag.index, idx);
    setDrag(null); setOverIndex(null);
  };

  const exec = (cmd, arg) => { document.execCommand(cmd, false, arg || null); };

  const selected = list.find((b) => b.id === selectedId);

  return (
    <div className="grid grid-cols-[150px_1fr_260px] gap-3 border border-slate-200 rounded-xl overflow-hidden" data-testid="email-builder">
      {/* Palette */}
      <div className="bg-slate-50 border-r border-slate-200 p-2 space-y-1.5" data-testid="builder-palette">
        <p className="text-[10px] uppercase text-slate-400 font-semibold px-1 mb-1">Blocks</p>
        {BLOCK_TYPES.map((bt) => {
          const I = ICONS[bt.type];
          return (
            <button key={bt.type} type="button"
              draggable
              onDragStart={() => setDrag({ kind: 'new', type: bt.type })}
              onDragEnd={() => { setDrag(null); setOverIndex(null); }}
              onClick={() => addAt(bt.type)}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-medium hover:border-[#00A79D] hover:text-[#00A79D] cursor-grab active:cursor-grabbing transition-colors"
              data-testid={`builder-add-${bt.type}`}>
              <I className="w-4 h-4" />{bt.label}
            </button>
          );
        })}
      </div>

      {/* Canvas */}
      <div className="bg-[#f5f8fa] p-4 min-h-[420px] max-h-[560px] overflow-auto"
        onDragOver={(e) => { e.preventDefault(); if (list.length === 0) setOverIndex(0); }}
        onDrop={onDrop}
        data-testid="builder-canvas">
        <div className="mx-auto bg-white rounded-xl shadow-sm" style={{ maxWidth: 600 }}>
          {list.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-16 border-2 border-dashed border-slate-200 rounded-xl m-3">
              Drag a block here, or click a block on the left to add it.
            </div>
          )}
          {list.map((b, i) => (
            <div key={b.id}
              onDragOver={(e) => onRowDragOver(i, e)}
              onClick={() => setSelectedId(b.id)}
              className={`relative group ${overIndex === i ? 'border-t-2 border-[#00A79D]' : 'border-t-2 border-transparent'}`}>
              {/* controls */}
              <div className={`absolute -left-0 top-1 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 ${selectedId === b.id ? 'opacity-100' : ''}`}>
                <span draggable onDragStart={() => setDrag({ kind: 'move', index: i })} onDragEnd={() => { setDrag(null); setOverIndex(null); }}
                  className="w-6 h-6 bg-[#0B2942] text-white rounded flex items-center justify-center cursor-grab active:cursor-grabbing" title="Drag to reorder"
                  data-testid={`builder-block-handle-${i}`}>
                  <GripVertical className="w-3.5 h-3.5" />
                </span>
              </div>
              <div className={`absolute right-1 top-1 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 ${selectedId === b.id ? 'opacity-100' : ''}`}>
                <button type="button" onClick={(e) => { e.stopPropagation(); duplicate(b.id); }} className="w-6 h-6 bg-white border border-slate-200 rounded flex items-center justify-center text-slate-500 hover:text-slate-800" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(b.id); }} className="w-6 h-6 bg-white border border-slate-200 rounded flex items-center justify-center text-red-500 hover:text-red-700" title="Delete" data-testid={`builder-block-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className={`${selectedId === b.id ? 'ring-2 ring-[#00A79D] ring-inset' : ''}`}>
                <BlockView b={b} patch={patch} />
              </div>
            </div>
          ))}
          {/* tail drop zone */}
          <div className={`h-4 ${overIndex === list.length ? 'border-t-2 border-[#00A79D]' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(list.length); }} />
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white border-l border-slate-200 p-3 overflow-auto max-h-[560px]" data-testid="builder-settings">
        {!selected ? (
          <p className="text-xs text-slate-400 mt-2">Select a block to edit its style.</p>
        ) : (
          <>
            <p className="text-xs font-bold text-[#0B2942] uppercase mb-3">{selected.type} settings</p>
            {(selected.type === 'text' || selected.type === 'columns') && (
              <Field label="Format (select text first)">
                <div className="flex gap-1">
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-600"><Bold className="w-4 h-4" /></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-600"><Italic className="w-4 h-4" /></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); const u = window.prompt('Link URL'); if (u) exec('createLink', u); }} className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-600"><Link2 className="w-4 h-4" /></button>
                </div>
              </Field>
            )}
            {selected.type === 'heading' && (
              <Field label="Level">
                <select className={inputCls} value={selected.level} onChange={(e) => patch(selected.id, { level: e.target.value })}>
                  <option value="h1">H1 - Large</option><option value="h2">H2 - Medium</option><option value="h3">H3 - Small</option>
                </select>
              </Field>
            )}
            {selected.type === 'image' && (
              <>
                <Field label="Upload or pick"><ImagePicker onPick={(url) => patch(selected.id, { src: url })} /></Field>
                <Field label="Image URL"><input className={inputCls} value={selected.src} placeholder="https://…" onChange={(e) => patch(selected.id, { src: e.target.value })} data-testid="builder-image-src" /></Field>
                <Field label="Alt text"><input className={inputCls} value={selected.alt} onChange={(e) => patch(selected.id, { alt: e.target.value })} /></Field>
                <Field label="Link URL (optional)"><input className={inputCls} value={selected.linkUrl} onChange={(e) => patch(selected.id, { linkUrl: e.target.value })} /></Field>
                <Field label={`Width: ${selected.width}%`}><input type="range" min="20" max="100" value={selected.width} onChange={(e) => patch(selected.id, { width: Number(e.target.value) })} className="w-full" /></Field>
              </>
            )}
            {selected.type === 'button' && (
              <>
                <Field label="Link URL"><input className={inputCls} value={selected.url} onChange={(e) => patch(selected.id, { url: e.target.value })} data-testid="builder-button-url" /></Field>
                <Field label="Background"><input type="color" value={selected.bg} onChange={(e) => patch(selected.id, { bg: e.target.value })} className="w-full h-9 rounded" /></Field>
                <Field label="Text colour"><input type="color" value={selected.color} onChange={(e) => patch(selected.id, { color: e.target.value })} className="w-full h-9 rounded" /></Field>
                <Field label={`Corner radius: ${selected.radius}px`}><input type="range" min="0" max="30" value={selected.radius} onChange={(e) => patch(selected.id, { radius: Number(e.target.value) })} className="w-full" /></Field>
              </>
            )}
            {selected.type === 'divider' && (
              <>
                <Field label="Colour"><input type="color" value={selected.color} onChange={(e) => patch(selected.id, { color: e.target.value })} className="w-full h-9 rounded" /></Field>
                <Field label={`Thickness: ${selected.thickness}px`}><input type="range" min="1" max="8" value={selected.thickness} onChange={(e) => patch(selected.id, { thickness: Number(e.target.value) })} className="w-full" /></Field>
              </>
            )}
            {selected.type === 'spacer' && (
              <Field label={`Height: ${selected.height}px`}><input type="range" min="8" max="80" value={selected.height} onChange={(e) => patch(selected.id, { height: Number(e.target.value) })} className="w-full" /></Field>
            )}
            {(selected.type === 'heading' || selected.type === 'text' || selected.type === 'columns') && (
              <>
                <Field label="Text colour"><input type="color" value={selected.color} onChange={(e) => patch(selected.id, { color: e.target.value })} className="w-full h-9 rounded" /></Field>
                <Field label={`Font size: ${selected.fontSize}px`}><input type="range" min="11" max="40" value={selected.fontSize} onChange={(e) => patch(selected.id, { fontSize: Number(e.target.value) })} className="w-full" /></Field>
              </>
            )}
            {(selected.type === 'heading' || selected.type === 'text' || selected.type === 'image' || selected.type === 'button') && (
              <Field label="Align"><AlignPicker value={selected.align} onChange={(v) => patch(selected.id, { align: v })} /></Field>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const BlockView = ({ b, patch }) => {
  const FONT = { fontFamily: 'Arial, Helvetica, sans-serif' };
  if (b.type === 'heading') {
    const size = b.level === 'h1' ? Math.max(b.fontSize, 30) : b.level === 'h3' ? Math.min(b.fontSize, 20) : b.fontSize;
    return (
      <div style={{ padding: '14px 28px 6px' }}>
        <Editable html={b.text} onCommit={(v) => patch(b.id, { text: v.replace(/<[^>]+>/g, '') })}
          style={{ ...FONT, color: b.color, fontSize: size, fontWeight: 700, textAlign: b.align, lineHeight: 1.25 }} />
      </div>
    );
  }
  if (b.type === 'text') {
    return (
      <div style={{ padding: '8px 28px' }}>
        <Editable html={b.html} onCommit={(v) => patch(b.id, { html: v })}
          style={{ ...FONT, color: b.color, fontSize: b.fontSize, lineHeight: 1.6, textAlign: b.align }} />
      </div>
    );
  }
  if (b.type === 'image') {
    return (
      <div style={{ padding: '12px 28px', textAlign: b.align }}>
        {b.src ? <img src={b.src} alt={b.alt} style={{ width: `${b.width}%`, maxWidth: '100%', display: 'inline-block' }} />
          : <div style={{ background: '#eef2f6', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#94a3b8', ...FONT, fontSize: 13, textAlign: 'center', padding: 28 }}>Add an image URL in settings →</div>}
      </div>
    );
  }
  if (b.type === 'button') {
    return (
      <div style={{ padding: '16px 28px', textAlign: b.align }}>
        <Editable html={b.label} onCommit={(v) => patch(b.id, { label: v.replace(/<[^>]+>/g, '') })}
          style={{ display: 'inline-block', background: b.bg, color: b.color, padding: '13px 28px', borderRadius: b.radius, ...FONT, fontWeight: 700, fontSize: 15 }} />
      </div>
    );
  }
  if (b.type === 'divider') {
    return <div style={{ padding: '8px 28px' }}><div style={{ borderTop: `${b.thickness}px solid ${b.color}` }} /></div>;
  }
  if (b.type === 'spacer') {
    return <div style={{ height: b.height }} className="bg-[repeating-linear-gradient(45deg,#f8fafc,#f8fafc_6px,#f1f5f9_6px,#f1f5f9_12px)]" />;
  }
  if (b.type === 'columns') {
    return (
      <div style={{ padding: '8px 28px', display: 'flex', gap: 20 }}>
        <Editable html={b.left} onCommit={(v) => patch(b.id, { left: v })} className="flex-1" style={{ ...FONT, color: b.color, fontSize: b.fontSize, lineHeight: 1.6 }} />
        <Editable html={b.right} onCommit={(v) => patch(b.id, { right: v })} className="flex-1" style={{ ...FONT, color: b.color, fontSize: b.fontSize, lineHeight: 1.6 }} />
      </div>
    );
  }
  return null;
};
